import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  Plus,
  Search,
  Calendar,
  Wallet,
  Smartphone,
  Landmark,
  BadgeDollarSign,
  Receipt,
  ChevronDown,
  ChevronUp,
  UserRound,
  X,
} from "lucide-react";

import { toast } from "react-toastify";

import NewPaymentModalModern from "./NewPaymentModalModern";

import "./styles/payments.css";

const API_BASE =
  "https://receivablesbackend.onrender.com/api";

const PaymentsModern = () => {

  const [payments, setPayments] =
    useState([]);

  const [clients, setClients] =
    useState([]);

  const [selectedClient, setSelectedClient] =
    useState(null);

  const [clientInvoices, setClientInvoices] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [showModal, setShowModal] =
    useState(false);

  const [showClientModal, setShowClientModal] =
    useState(false);

  const [expandedRow, setExpandedRow] =
    useState(null);

  const [search, setSearch] =
    useState("");

  const [clientSearch, setClientSearch] =
    useState("");

  const [filterMonth, setFilterMonth] =
    useState("");

  // =====================================================
  // FETCH PAYMENTS
  // =====================================================

  const fetchPayments = async () => {

    try {

      setLoading(true);

      const res = await fetch(
        `${API_BASE}/payments`
      );

      if (!res.ok)
        throw new Error();

      const data = await res.json();

      const sorted = data.sort(
        (a, b) =>
          new Date(b.payment_date) -
          new Date(a.payment_date)
      );

      setPayments(sorted);

    } catch (err) {

      console.error(err);

      toast.error(
        "Failed to load payments."
      );

    } finally {

      setLoading(false);

    }
  };

  // =====================================================
  // FETCH CLIENTS
  // =====================================================

const fetchClients = async () => {

  try {

    const res = await fetch(
      `${API_BASE}/customers`
    );

    if (!res.ok)
      throw new Error();

    const data = await res.json();

    setClients(data);

  } catch (err) {

    console.error(err);

    toast.error(
      "Failed to load clients."
    );
  }
};

  // =====================================================
  // FETCH CLIENT INVOICES
  // =====================================================

  const fetchClientInvoices =
    async (clientId) => {

      try {

        const res = await fetch(
          `${API_BASE}/invoices`
        );

        if (!res.ok)
          throw new Error();

        const data =
          await res.json();

        const filtered =
          data.filter(
            (inv) =>
              Number(
                inv.pos_customer_id
              ) ===
              Number(clientId)
          );

        setClientInvoices(
          filtered
        );

      } catch (err) {

        console.error(err);

        toast.error(
          "Failed to load invoices."
        );
      }
    };

  useEffect(() => {

    fetchPayments();

    fetchClients();

  }, []);

  // =====================================================
  // FILTER PAYMENTS
  // =====================================================

  const filteredPayments =
    payments.filter((p) => {

      const matchesSearch =
        p.client_name
          ?.toLowerCase()
          .includes(
            search.toLowerCase()
          ) ||
        p.invoice_number
          ?.toLowerCase()
          .includes(
            search.toLowerCase()
          ) ||
        p.mode
          ?.toLowerCase()
          .includes(
            search.toLowerCase()
          );

      if (!filterMonth)
        return matchesSearch;

      const d = new Date(
        p.payment_date
      );

      const [year, month] =
        filterMonth.split("-");

      const matchesMonth =
        d.getFullYear() ===
          Number(year) &&
        d.getMonth() + 1 ===
          Number(month);

      return (
        matchesSearch &&
        matchesMonth
      );
    });

  // =====================================================
  // FILTER CLIENTS
  // =====================================================

  const filteredClients =
    clients.filter((c) =>
      c.name
        ?.toLowerCase()
        .includes(
          clientSearch.toLowerCase()
        )
    );

  // =====================================================
  // SUMMARY STATS
  // =====================================================

  const totalPayments = useMemo(() => {

    return payments.reduce(
      (sum, p) =>
        sum +
        Number(p.amount || 0),
      0
    );

  }, [payments]);

  const thisMonthPayments =
    useMemo(() => {

      const now = new Date();

      return payments
        .filter((p) => {

          const d = new Date(
            p.payment_date
          );

          return (
            d.getMonth() ===
              now.getMonth() &&
            d.getFullYear() ===
              now.getFullYear()
          );
        })

        .reduce(
          (sum, p) =>
            sum +
            Number(p.amount || 0),
          0
        );

    }, [payments]);

  const outstandingBalance =
    useMemo(() => {

      return payments.reduce(
        (sum, p) =>
          sum +
          Number(
            p.balance_outstanding ||
              0
          ),
        0
      );

    }, [payments]);

  // =====================================================
  // PAYMENT MODE ICON
  // =====================================================

  const getPaymentIcon = (mode) => {

    switch (mode) {

      case "Cash":
        return <Wallet size={18} />;

      case "MMG":
        return (
          <Smartphone size={18} />
        );

      case "Bank":
        return (
          <Landmark size={18} />
        );

      case "Account Credit":
        return (
          <BadgeDollarSign
            size={18}
          />
        );

      default:
        return <Wallet size={18} />;
    }
  };

  // =====================================================
  // OPEN CLIENT FLOW
  // =====================================================

  const handleSelectClient =
    async (client) => {

      setSelectedClient(client);

      await fetchClientInvoices(
        client.id
      );

      setShowClientModal(false);

      setShowModal(true);
    };

  // =====================================================
  // LOADING
  // =====================================================

  if (loading) {

    return (
      <div className="modern-loading-state">

        <div className="loader-circle" />

        <p>Loading payments...</p>

      </div>
    );
  }

  return (
    <div className="modern-page-container">

      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <div className="modern-page-header">

        <div>

          <h1>Payments</h1>

          <p>
            Manage and monitor
            customer payments
          </p>

        </div>

        <button
          className="modern-primary-btn"
          onClick={() =>
            setShowClientModal(true)
          }
        >
          <Plus size={18} />
          New Payment
        </button>

      </div>

      {/* ================================================= */}
      {/* STATS */}
      {/* ================================================= */}

      <div className="payments-stats-grid">

        <div className="modern-stat-card">

          <div className="stat-icon">
            <Wallet size={20} />
          </div>

          <div>

            <span>
              Total Payments
            </span>

            <h2>
              $
              {totalPayments.toLocaleString(
                "en-US",
                {
                  minimumFractionDigits: 2,
                }
              )}
            </h2>

          </div>

        </div>

        <div className="modern-stat-card">

          <div className="stat-icon">
            <Calendar size={20} />
          </div>

          <div>

            <span>
              This Month
            </span>

            <h2>
              $
              {thisMonthPayments.toLocaleString(
                "en-US",
                {
                  minimumFractionDigits: 2,
                }
              )}
            </h2>

          </div>

        </div>

        <div className="modern-stat-card">

          <div className="stat-icon">
            <Receipt size={20} />
          </div>

          <div>

            <span>
              Outstanding
            </span>

            <h2>
              $
              {outstandingBalance.toLocaleString(
                "en-US",
                {
                  minimumFractionDigits: 2,
                }
              )}
            </h2>

          </div>

        </div>

      </div>

      {/* ================================================= */}
      {/* TOOLBAR */}
      {/* ================================================= */}

      <div className="payments-toolbar">

        <div className="modern-search-box">

          <Search size={18} />

          <input
            type="text"
            placeholder="Search payments..."
            value={search}
            onChange={(e) =>
              setSearch(
                e.target.value
              )
            }
          />

        </div>

        <div className="month-filter-box">

          <Calendar size={18} />

          <input
            type="month"
            value={filterMonth}
            onChange={(e) =>
              setFilterMonth(
                e.target.value
              )
            }
          />

        </div>

      </div>

      {/* ================================================= */}
      {/* PAYMENTS GRID */}
      {/* ================================================= */}

      <div className="payments-grid">

        {filteredPayments.length >
        0 ? (

          filteredPayments.map((p) => {

            const expanded =
              expandedRow === p.id;

            return (
              <div
                key={p.id}
                className={`modern-payment-card ${
                  expanded
                    ? "expanded-payment-card"
                    : ""
                }`}
              >

                <div
                  className="payment-card-main"
                  onClick={() =>
                    setExpandedRow(
                      expanded
                        ? null
                        : p.id
                    )
                  }
                >

                  <div className="payment-card-left">

                    <div className="payment-avatar">

                      {p.client_name
                        ?.charAt(0)
                        ?.toUpperCase()}

                    </div>

                    <div>

                      <h3>
                        {p.client_name}
                      </h3>

                      <p>
                        Invoice #
                        {
                          p.invoice_number
                        }
                      </p>

                    </div>

                  </div>

                  <div className="payment-card-right">

                    <div
                      className={`payment-mode-badge ${
                        p.mode
                          ?.toLowerCase()
                          .replace(
                            /\s/g,
                            "-"
                          )
                      }`}
                    >

                      {getPaymentIcon(
                        p.mode
                      )}

                      {p.mode}

                    </div>

                    <h2>
                      $
                      {Number(
                        p.amount || 0
                      ).toLocaleString(
                        "en-US",
                        {
                          minimumFractionDigits: 2,
                        }
                      )}
                    </h2>

                    <span>
                      Remaining:
                      $
                      {Number(
                        p.balance_outstanding ||
                          0
                      ).toLocaleString(
                        "en-US",
                        {
                          minimumFractionDigits: 2,
                        }
                      )}
                    </span>

                    <small>
                      {new Date(
                        p.payment_date
                      ).toLocaleDateString()}
                    </small>

                  </div>

                  <button className="expand-btn">

                    {expanded ? (
                      <ChevronUp
                        size={18}
                      />
                    ) : (
                      <ChevronDown
                        size={18}
                      />
                    )}

                  </button>

                </div>

                {expanded && (

                  <div className="payment-expanded-details">

                    <div className="detail-item">

                      <span>
                        Payment ID
                      </span>

                      <strong>
                        #{p.id}
                      </strong>

                    </div>

                    <div className="detail-item">

                      <span>
                        Remarks
                      </span>

                      <strong>
                        {p.remarks ||
                          "None"}
                      </strong>

                    </div>

                  </div>

                )}

              </div>
            );
          })

        ) : (

          <div className="modern-empty-state">

            <Receipt size={50} />

            <h2>
              No Payments Found
            </h2>

            <p>
              Payments will appear
              here once recorded.
            </p>

          </div>

        )}

      </div>

      {/* ================================================= */}
      {/* CLIENT SELECT MODAL */}
      {/* ================================================= */}

      {showClientModal && (

        <div className="modern-modal-overlay">

          <div className="client-select-modal">

            <div className="modern-modal-header">

              <div>

                <h2>
                  Select Client
                </h2>

                <p>
                  Start a new payment
                </p>

              </div>

              <button
                className="close-btn"
                onClick={() =>
                  setShowClientModal(
                    false
                  )
                }
              >
                <X size={18} />
              </button>

            </div>

            <div className="modern-search-box">

              <Search size={18} />

              <input
                type="text"
                placeholder="Search client..."
                value={clientSearch}
                onChange={(e) =>
                  setClientSearch(
                    e.target.value
                  )
                }
              />

            </div>

            <div className="client-selection-list">

              {filteredClients.map(
                (client) => (

                  <button
                    key={client.id}
                    className="client-select-card"
                    onClick={() =>
                      handleSelectClient(
                        client
                      )
                    }
                  >

                    <div className="client-avatar">

                      <UserRound
                        size={18}
                      />

                    </div>

                    <div>

                      <h4>
                        {client.name}
                      </h4>

                      <p>
                        Credit: $
                        {Number(
                          client.credit_balance ||
                            0
                        ).toLocaleString(
                          "en-US",
                          {
                            minimumFractionDigits: 2,
                          }
                        )}
                      </p>

                    </div>

                  </button>
                )
              )}

            </div>

          </div>

        </div>

      )}

      {/* ================================================= */}
      {/* PAYMENT MODAL */}
      {/* ================================================= */}

      <NewPaymentModalModern
        isOpen={showModal}
        onClose={() =>
          setShowModal(false)
        }
        client={selectedClient}
        invoices={clientInvoices}
        payments={payments}
        onSuccess={() => {

          fetchPayments();

          setShowModal(false);
        }}
      />

    </div>
  );
};

export default PaymentsModern;